import axios, {
    AxiosError,
    AxiosRequestConfig,
    AxiosResponse,
    Method,
} from "axios";

import { MIME_TYPE } from "../constants/files";
import { LOCAL_STORAGE_KEY } from "../constants/localStorageKeys";

const instance = axios.create({
    baseURL: "/api/v1/",
    xsrfHeaderName: "x-csrf-token",
    headers: {
        "content-type": MIME_TYPE.JSON,
    },
});

instance.interceptors.request.use((config) => {
    if (
        config.method !== "GET" &&
        localStorage.getItem(LOCAL_STORAGE_KEY.CSRF_TOKEN)
    ) {
        return {
            ...config,
            headers: {
                ...config.headers,
                "x-csrf-token": localStorage.getItem(
                    LOCAL_STORAGE_KEY.CSRF_TOKEN,
                ),
            },
        };
    }
    return config;
});

instance.interceptors.response.use((res) => {
    if (res.headers["x-csrf-token"]) {
        localStorage.setItem(
            LOCAL_STORAGE_KEY.CSRF_TOKEN,
            res.headers["x-csrf-token"],
        );
    }
    return res;
});

const request = async <RES, REQ = undefined>(
    url: string,
    method: Method = "GET",
    data?: REQ,
    config?: AxiosRequestConfig,
): Promise<RES> => {
    try {
        const { data: res } = await instance.request<
            RES,
            AxiosResponse<RES>,
            REQ
        >({
            url,
            method,
            data,
            ...(config || {}),
        });
        return res;
    } catch (error) {
        console.error(error);

        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError<Record<string, unknown>>;
            if (axiosError.response) {
                throw {
                    errors: [],
                    status: axiosError.response.status,
                    ...axiosError.response.data,
                };
            }
        }
        throw {
            errors: [
                {
                    enMessage: "unknown error",
                    key: "Error.Unknown",
                    valuesStr: [],
                },
            ],
        };
    }
};

export const requestWithToken =
    <RES>(token: string) =>
    (url: string): Promise<RES> =>
        request(url, "GET", undefined, {
            headers: {
                "Access-Token": token,
            },
        });

type ErrorObject = {
    enMessage: string;
    key: string;
    valuesStr: string[];
};

export type BaseApiError = {
    debug?: string;
    errors: ErrorObject[];
};

export type ApiError = BaseApiError & {
    status: number;
};

export type ApiResponse<T> = T | ApiError;

export const isApiError = <T>(res: ApiResponse<T> | Error): res is ApiError => {
    return (res as ApiError).errors !== undefined;
};

export const AccessTokenHeader = "Access-Token";

export default request;
